import { create } from 'zustand'
import { persist, PersistOptions } from 'zustand/middleware'

const STORE_NAME = 'step-store'

export interface BookingFlowStep<StepDataType = Record<string, unknown>> {
  number: number
  data: StepDataType
  completed?: boolean
}

export interface BookingFlowStore<StepDataType = Record<string, unknown>> {
  /**
   * Indicates whether the store has been hydrated with persisted state
   * This is used to prevent accessing the store before it has been hydrated
   */
  hasHydrated?: boolean
  /**
   * Contains all steps data
   */
  steps: BookingFlowStep<StepDataType>[]
  /**
   * Clears all steps data in the store
   */
  clearSteps: () => void
  /**
   * Sets the hydration state of the store
   * This is used to indicate that the store has been hydrated with persisted state
   */
  setHasHydrated: (state: boolean) => void
  /**
   * Updates step data that has the given step number
   */
  updateStep: (stepNumber: number, newStepData: BookingFlowStep<StepDataType>['data']) => void
  /**
   * Updates `completed` state of a step in the store
   */
  setStepCompleted: (stepNumber: number, completed?: boolean) => void
  /**
   * Gets the step data with the provided step number
   */
  getStep: (stepNumber: number) => BookingFlowStep<StepDataType> | undefined
}

const useStepStore = create<BookingFlowStore>()(
  persist(
    (set, get) => ({
      hasHydrated: false,

      steps: [],

      clearSteps: () => set({ steps: [] }),

      setHasHydrated: (state) => {
        set({
          hasHydrated: state,
        })
      },

      updateStep: (stepNumber, data) => {
        set((state) => {
          const targetStepIndex = state.steps.findIndex(({ number }) => number === stepNumber)

          if (targetStepIndex > -1) {
            state.steps[targetStepIndex] = {
              number: stepNumber,
              data,
            }
          } else {
            state.steps.push({
              number: stepNumber,
              data,
            })
          }

          return { steps: state.steps }
        })
      },

      setStepCompleted: (stepNumber, completed) => {
        set((state) => {
          const stepIndex = state.steps.findIndex((step) => step.number === stepNumber)
          if (stepIndex > -1) {
            state.steps[stepIndex].completed = completed
          }
          return { steps: state.steps }
        })
      },

      getStep: (stepNumber) => get().steps?.find(({ number }) => number === stepNumber),
    }),
    {
      name: STORE_NAME,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    } as PersistOptions<BookingFlowStore>,
  ),
)

export default useStepStore
